import React, { useEffect, useMemo, useState } from 'react'

import useLogAndCaptureError from '../hooks/useLogAndCaptureError'
import useSiteMetadata from '../hooks/useSiteMetadata'
import { isMagentoScopeNotGrantedError } from '../lib/auth/errors'
import { CartContext, useCartGet } from '../lib/cart'
import {
  autoRemoveErrantCartItems$,
  cartItemCheckSetFromFeatureFlags,
  latestCartItemErrorsForRemoval$,
} from '../lib/cart/state/cart-items/cart-item-remove'
import {
  autoUpdateErrantCartItems$,
  cartItemsUpdating$,
  latestCartItemErrorsForUpdate$,
} from '../lib/cart/state/cart-items/cart-item-update'
import { globalPromotionsMachine$, setGlobalPromotions } from '../lib/cart/state/global-promos'

const CartContextProvider: React.FC = ({ children }) => {
  const siteMetadata = useSiteMetadata()
  const [allowGuestCart, setAllowGuestCart] = useState(
    siteMetadata?.featureFlags?.anonymousCart ?? false,
  )
  const { cart, error, loading, refetch, type } = useCartGet({ allowGuestCart })

  // no need to log and capture error if scope not granted
  useLogAndCaptureError(!isMagentoScopeNotGrantedError(error) ? error : undefined)

  useEffect(() => {
    const { featureFlags, globalPromotions } = siteMetadata ?? {}
    const checkSet = cartItemCheckSetFromFeatureFlags(featureFlags)
    const subscriptions = [
      latestCartItemErrorsForUpdate$.subscribe(),
      autoUpdateErrantCartItems$().subscribe(),
      cartItemsUpdating$.subscribe(),
      // manage global promotions in cart (add/remove according to dispensary opt-in)
      globalPromotionsMachine$.subscribe(),
    ]
    if (checkSet) {
      subscriptions.push(latestCartItemErrorsForRemoval$(checkSet).subscribe())
      subscriptions.push(autoRemoveErrantCartItems$(checkSet).subscribe())
    }
    // set global promotions input after creating subscription to machine
    setGlobalPromotions(globalPromotions ?? [])
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [siteMetadata])

  const cartContext = useMemo(
    () => ({ cart, error, loading, setAllowGuestCart, refetch, type }),
    [cart, error, loading, refetch, type],
  )

  return <CartContext.Provider value={cartContext}>{children}</CartContext.Provider>
}

export default CartContextProvider
